import Keycloak from 'keycloak-js'
import { getConfig } from '../config'

const config = getConfig()

export interface AuthUser {
  id: string
  username: string
  email: string
  locale?: string
}

class MockAuth {
  private user: AuthUser = {
    id: 'mock-user-id',
    username: 'testuser',
    email: 'test@example.com',
    locale: 'pt' // Default to Portuguese for mock
  }

  async init(): Promise<boolean> {
    return true
  }

  isAuthenticated(): boolean {
    return true
  }

  getUser(): AuthUser {
    return this.user
  }

  getToken(): string {
    return 'mock-token-123'
  }

  async logout(): Promise<void> {
    // Do nothing in mock mode
  }

  openAccountPage(): void {
    // Do nothing in mock mode
  }

  async login(): Promise<void> {
    // Do nothing in mock mode
  }

  getUserLanguage(): string {
    return this.user.locale || 'en'
  }
}

class KeycloakAuth {
  public keycloak: Keycloak

  constructor() {
    this.keycloak = new Keycloak({
      url: config.KEYCLOAK_URL,
      realm: config.KEYCLOAK_REALM,
      clientId: config.KEYCLOAK_CLIENT_ID
    })
  }

  async init(): Promise<boolean> {
    try {
      const authenticated = await this.keycloak.init({
        onLoad: 'login-required',
        checkLoginIframe: false,
        enableLogging: process.env.NODE_ENV === 'development',
        pkceMethod: 'S256' // Use PKCE for better security
      })


      return authenticated
    } catch (error) {
      console.error('Failed to initialize Keycloak:', error)
      return false
    }
  }

  isAuthenticated(): boolean {
    return !!this.keycloak.authenticated
  }

  getUser(): AuthUser {
    const tokenParsed = this.keycloak.tokenParsed
    return {
      id: tokenParsed?.sub || '',
      username: tokenParsed?.preferred_username || '',
      email: tokenParsed?.email || '',
      locale: tokenParsed?.locale || 'en'
    }
  }

  getToken(): string {
    return this.keycloak.token || ''
  }

  async logout(): Promise<void> {
    await this.keycloak.logout()
  }

  openAccountPage(): void {
    if (this.keycloak.accountManagement) {
      this.keycloak.accountManagement()
    }
  }

  async login(): Promise<void> {
    await this.keycloak.login()
  }

  getUserLanguage(): string {
    const tokenParsed = this.keycloak.tokenParsed
    // Try to get language from token, fallback to 'pt'
    const locale = tokenParsed?.locale || 'pt'

    // Map common locale formats to our supported languages
    if (locale.startsWith('pt')) {
      return 'pt'
    }

    if (locale.startsWith('en')) {
      return 'en'
    }

    // Default to Portuguese if language is not recognized
    return 'pt'
  }
}

export const authService = config.USE_MOCKS ? new MockAuth() : new KeycloakAuth()
